import React, { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Contact } from "../../../../interfaces/DTO/crm/contact";
import { BeneficiaryInfo } from "../../../../interfaces/DTO/mislaka_xml_insurence_models";
import { usePartialUpdateContact } from "../../../../hooks/crm/useContacts";
import { beneficiaryName, definitionProblem, familyChildren, suggestBeneficiariesFromFamily } from "../../../../utils/beneficiariesUtil";
import { formatRelationType } from "../../../../utils/pensionEnumFormatter";
import BeneficiariesDefinitionEditor from "./BeneficiariesDefinitionEditor";

interface Props {
  contact: Contact;
  /** the client's spouse (a family's other member) - offered with the children as beneficiaries */
  spouse?: Contact;
}

/**
 * מוטבים – הגדרת המוטבים הכללית של הלקוח (Contact.data.beneficiaries): מה כל קופה שלו צריכה להגדיר.
 * מוצגת כרשימה; "ערוך" פותח את העורך. מכאן היא גם ההצעה בעדכון מוטבים בקופות (טבלת המוטבים).
 */
const ContactBeneficiariesTab: React.FC<Props> = ({ contact, spouse }) => {
  const saved = useMemo(() => contact.data?.beneficiaries ?? [], [contact.data?.beneficiaries]);
  /** edit mode - the definition being edited */
  const [draft, setDraft] = useState<BeneficiaryInfo[] | null>(null);
  const updateContact = usePartialUpdateContact();

  const familySuggestion = useMemo(
    () => suggestBeneficiariesFromFamily(spouse, familyChildren(contact, spouse)),
    [spouse, contact]
  );

  const changed = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);
  // an empty definition can be saved (cleared); otherwise only a complete one
  const problem = draft && draft.length > 0 ? definitionProblem(draft) : null;

  const save = async () => {
    if (!draft) return;
    // the whole data (a partial update replaces it as is); an empty definition is removed
    const data: Record<string, unknown> = { ...(contact.data ?? {}) };
    if (draft.length > 0) data.beneficiaries = draft;
    else delete data.beneficiaries;
    await updateContact.mutateAsync({ id: contact.id!, fields: { data } as Partial<Contact> });
    setDraft(null);
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Typography variant="body1" color="text.secondary">
הגדרת מוטבים רצויה        </Typography>
        {!draft && (
          <Button size="small" onClick={() => setDraft(saved)} sx={{ flexShrink: 0 }}>
            ערוך
          </Button>
        )}
      </Box>

      {draft ? (
        <>
          <BeneficiariesDefinitionEditor
            value={draft}
            onChange={setDraft}
            familySuggestion={familySuggestion}
            defaultLastName={contact.last_name ?? ""}
            disabled={updateContact.isPending}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={save}
              disabled={!changed || !!problem || updateContact.isPending}
              startIcon={updateContact.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              שמור
            </Button>
            <Button variant="outlined" onClick={() => setDraft(null)} disabled={updateContact.isPending}>
              חזור
            </Button>
          </Box>
        </>
      ) : saved.length === 0 ? (
        <Typography color="text.secondary">לא הוגדרו מוטבים</Typography>
      ) : (
        // view - one row per beneficiary
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {saved.map((b, i) => (
            <Box
              key={b.id_number || i}
              sx={{ display: "flex", alignItems: "center", gap: 2, px: 1.5, py: 1, borderRadius: 2, backgroundColor: "action.hover" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={600} noWrap>
                  {beneficiaryName(b)}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {[b.relation_type != null ? formatRelationType(b.relation_type) : null, b.id_number ? `ת.ז ${b.id_number}` : null]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={700}>
                {b.beneficiary_percentage ?? 0}%
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ContactBeneficiariesTab;
